/* eslint-disable no-unreachable */
// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { truncate } from 'lodash';
import { useForm } from 'react-hook-form';

import AddressesComponent from 'Components/Addresses/Addresses';
import { API_COUNTRIES, API_COUNTIES, API_ADDRESSES, API_ADDRESSES_CODE, COUNTRIES, EMAIL_REGEX } from 'Constants';
import { UserContext } from 'Contexts/UserContext';
import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useAsyncError from 'Hooks/useAsyncError';
import useNumberUtils from 'Hooks/useNumberUtils';
import usePermissions from 'Hooks/usePermissions';
import useAnalytics from 'Hooks/useAnalytics';

const countryId = 192; // Romania
const COUNTIES = 'counties';
const validationCodePattern = /^[0-9]{4}$/;

const defaultValues = {
  firstName: '',
  lastName: '',
  phone: '',
  recipientEmail: '',
  county: null,
  city: null,
  street: '',
  number: '',
  building: '',
  floor: '',
  apartment: '',
  postalCode: '',
  defaultDelivery: '',
  validationCode: '',
};

const Addresses = memo(() => {
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [isActionsOpen, setIsActionOpen] = useState(false);
  const [counties, setCounties] = useState(null);
  const [cities, setCities] = useState(null);
  const [postError, setPostError] = useState(null);
  const [search, setSearch] = useState('');
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [loadingDeleteId, setLoadingDeleteId] = useState('');
  const [edit, setEdit] = useState('');
  const [shouldShowDefaultDeliveryWarning, setShouldShowDefaultDeliveryWarning] = useState(false);
  const [errors, setErrors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [validationCodePending, setValidationCodePending] = useState(false);
  const [truncateEmail, setTruncateEmail] = useState('');

  const fetcher = useFetch();
  const { t } = useTranslation();
  const { cache } = useSWRConfig();
  const { location } = useRouter();
  const throwError = useAsyncError();
  const { formatDate } = useNumberUtils();
  const { isExternalReseller } = usePermissions();
  const analytics = useAnalytics();
  const {
    formState: { errors: rhfErrors },
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });
  const { email, loggedIn } = useContextSelector(UserContext, ({ data }) => ({
    email: data?.email,
    loggedIn: data?.loggedIn
  }));

  const countyValue = watch('county');
  const cityValue = watch('city');
  const defaultDeliveryValue = watch('defaultDelivery');
  const validationCodeValue = watch('validationCode');
  const recipientEmailValue = watch('recipientEmail');
  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });
  const pattern = value => ({ value, message: t('errors.filedInvalidFormat') });

  const validators = {
    firstName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(40),
    },
    lastName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(40),
    },
    phone: {
      required,
      pattern: pattern(COUNTRIES[countryId].phoneNumberRegex),
    },
    recipientEmail: {
      required: {
        value: !edit,
        message: t('errors.fieldBlank'),
      },
      pattern: {
        value: EMAIL_REGEX,
        message: t('errors.invalidEmail'),
      },
    },
    county: {
      required,
    },
    city: {
      required,
    },
    street: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(60),
    },
    number: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(10),
    },
    building: {
      maxLength: maxLength(10),
    },
    floor: {
      maxLength: maxLength(10),
    },
    apartment: {
      maxLength: maxLength(10),
    },
    postalCode: {
      required,
      pattern: pattern(COUNTRIES[countryId].zipCodeRegex),
      validate: {
        district: value => {
          if (countyValue && countyValue?.erpCountyId === '10' && cityValue) {
            // Extract the last 2 digits from cityValue
            const lastTwoDigitsCityValue = String(cityValue?.id).slice(-2);

            // Extract the first 2 digits from zipCode
            const firstTwoDigitsZipCode = String(value).slice(0, 2);

            return lastTwoDigitsCityValue === firstTwoDigitsZipCode || t('errors.postalCodeDistrict', { district: lastTwoDigitsCityValue });
          }
          return true;
        }
      }
    },
    validationCode: {
      pattern: pattern(validationCodePattern),
    }
  };

  useEffect(() => {
    if (addresses?.length > 0 && defaultDeliveryValue) {
      const approvedDefaultDelivery = addresses.find(address => address.defaultDelivery && address.status === 2);
      if (defaultDeliveryValue && approvedDefaultDelivery) {
        setShouldShowDefaultDeliveryWarning(true);
      } else {
        setShouldShowDefaultDeliveryWarning(false);
      }
    }
  }, [addresses, defaultDeliveryValue]);

  const getCounties = useCallback(
    debounce(() => fetcher(`${ API_COUNTRIES }/${ countryId }/${ COUNTIES }`), 250, { leading: true, trailing: false }),
    []
  );
  const getCities = useCallback(
    debounce(fetcher, 250, { leading: true, trailing: false }),
    []
  );

  const getAddresses = query => {
    setAddressesLoading(true);

    if (addresses?.length) {
      setAddresses([]);
    }

    fetcher(`${API_ADDRESSES}${query || ''}`).then(res => {
      if (!res.length) {
        setAddresses(null);
      } else {
        const mappedAddresses = res.map(item => ({
          ...item,
          fullAddress: `${ item.street ? `Str. ${ item.street }, ` : '' }${ item.number ? item.number : '' }`,
          countyCity: `${ item.county.name }${ item?.city ? `, ${item.city.name}` : '' }`,
          createdAt: formatDate(item.added, { timeStyle: undefined })
        }));
        setAddresses(mappedAddresses);
      }
    })
      .catch(err => throwError(err))
      .finally(() => setAddressesLoading(false));
  };

  const handleSearchAddress = e => {
    analytics.track('search_address');
    if (e && e.keyCode !== 13) return;

    if (search.length >= 3 || search === '') {
      const searchQuery = search === '' ? '' : `?search=${search}`;
      getAddresses(searchQuery);
    }
  };

  const handleSearchChange = value => {
    setSearch(value);
    if (value === '') {
      getAddresses();
    }
  };

  const handleAddAddress = value => {
    analytics.track('open_add_address');
    reset(defaultValues);
    setShouldShowDefaultDeliveryWarning(false);
    setEdit('');
    setIsActionOpen(value);
  };

  const handleDeleteAddress = addressId => {
    analytics.track('delete_address');
    setLoadingDeleteId(addressId);
    setErrors([]);
    fetcher(`${API_ADDRESSES}/${addressId}`, { method: 'DELETE' })
      .then(() => {
        getAddresses();
        setErrors([{ message: t('pages.messages.deleteSuccess'), type: 'success' }]);
      })
      .catch(err => throwError(err))
      .finally(() => setLoadingDeleteId(''));
  };

  const handleEditAddress = addressId => {
    analytics.track('edit_address');
    setEdit(addressId);
    setIsActionOpen(true);
    const {
      firstName,
      lastName,
      phone,
      email: recipientEmail,
      county,
      city,
      street,
      number,
      building,
      floor,
      apartment,
      postalCode,
      defaultDelivery
    } = addresses.find(address => address.addressId === addressId);

    reset({
      firstName,
      lastName,
      phone,
      recipientEmail,
      county,
      city,
      street,
      number,
      building,
      floor,
      apartment,
      postalCode,
      defaultDelivery
    });
  };

  // Get Addresses Requests on page load
  useEffect(() => {
    if (!addresses.length && loggedIn) {
      getAddresses();
    }
  }, [location.pathname, email, loggedIn]);

  // Get counties on load
  useEffect(() => {
    if (!counties && loggedIn) {
      getCounties().then(res => {
        if (res) {
          setCounties(res);
        }
      });
    }
  }, [email, loggedIn]);

  useEffect(() => {
    if (countyValue) {
      const county = (counties || []).find(c => c.erpCountyId === countyValue?.erpCountyId);
      const key = `${ API_COUNTRIES }/${ countryId }/${ COUNTIES }/${ county.erpCountyId }/cities`;
      if (!cache.has(key)) {
        getCities(key).then(res => {
          cache.set(key, res);
          setCities(res);
        });
      } else {
        const cachedCities = cache.get(key);
        setCities(cachedCities);
      }
    }
  }, [countyValue, counties]);

  useEffect(() => {
    if (email && email?.length) {
      const [name, domain] = email.split('@');
      setTruncateEmail(`${ truncate(name, { length: 5 }) }@${ domain }`);
    }
  }, [email]);

  const handleOnSetCounty = ({ selectedItem }) => {
    setValue('county', selectedItem);
    setCities(
      selectedItem?.id
        ? cache.get(`${ API_COUNTIES }/${ selectedItem.id }/cities`) || []
        : null
    );
    setValue('city', null);
  };

  const handleSubmit = async ({ isNewAddress, withValidationCode }) => {
    analytics.track('submit_address');
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    const optionalOnEdit = ['firstName', 'lastName', 'phone'];

    if (rhfErrorsKeys.length && (
      (edit && !rhfErrorsKeys.every(element => optionalOnEdit.includes(element)))
      || !edit
    )) {
      // Stop execution if any errors or edit is true and other fields besides (firstName, lastName and phone) have error
      return;
    }

    if (isNewAddress && !withValidationCode && !modalOpen) {
      setValidationCodePending(true);
      await fetcher(API_ADDRESSES_CODE, {
        method: 'POST',
        body: JSON.stringify({}),
      })
        .then(() => setModalOpen(true))
        .finally(() => setValidationCodePending(false));

      // Stop execution
      return;
    }

    const {
      firstName,
      lastName,
      county,
      city,
      phone,
      recipientEmail,
      street,
      number,
      building,
      floor,
      apartment,
      postalCode,
      defaultDelivery,
      validationCode
    } = getValues();

    const data = {
      firstName,
      lastName,
      phone,
      email: recipientEmail,
      county: county?.erpCountyId,
      city: city?.id,
      street,
      number,
      building,
      floor,
      apartment,
      postalCode,
      defaultDelivery: !!defaultDelivery,
      validationCode: withValidationCode ? validationCode : undefined
    };

    setIsRequestPending(true);
    setErrors([]);
    setPostError([]);

    let path = API_ADDRESSES;
    let method = 'POST';

    if (edit) {
      path = `${API_ADDRESSES}/${edit}`;
      method = 'PUT';
      data.addressId = edit;
    }

    fetcher(path, {
      method,
      body: JSON.stringify(data),
    }).then(() => {
      let message = { message: t('pages.messages.addSuccess'), type: 'success' };
      if (!data.validationCode) {
        message = { message: t('pages.messages.addWait'), type: 'wait' };
      }
      if (data?.addressId) {
        message = { message: t('pages.messages.editSuccess'), type: 'success' };
      }
      getAddresses();
      setIsActionOpen(false);
      setErrors([message]);
      setModalOpen(false);
    }).catch(err => {
      const hasOnlyValidationCodeError = err?.info?.errors?.length === 1 && (err?.info?.errors || []).some(error => error.code.includes('validation_code'));
      if (!hasOnlyValidationCodeError) {
        setModalOpen(false);
        setValue('validationCode', '');
        setErrors([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]);
      } else {
        setPostError([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]);
      }
    })
      .finally(() => {
        setIsRequestPending(false);
      });
  };

  return (
    <AddressesComponent
      addresses={ addresses || [] }
      addressesLoading={ addressesLoading }
      isActionsOpen={ isActionsOpen }
      selectedCounty={ countyValue }
      counties={ counties }
      handleSelectedCounty={ handleOnSetCounty }
      selectedCity={ cityValue }
      cities={ cities }
      loadingCities={ Array.isArray(cities) && cities?.length === 0 }
      handleSubmit={ handleSubmit }
      postError={ postError }
      handleSearchAddress={ handleSearchAddress }
      handleDeleteAddress={ handleDeleteAddress }
      handleSearchChange={ handleSearchChange }
      handleEditAddress={ handleEditAddress }
      handleAddAddress={ handleAddAddress }
      isRequestPending={ isRequestPending }
      search={ search }
      setSearch={ setSearch }
      loadingDelete={ loadingDeleteId }
      isExternalReseller={ isExternalReseller }
      shouldShowDefaultDeliveryWarning={ shouldShowDefaultDeliveryWarning }
      errors={ errors }
      email={ truncateEmail }
      edit={ edit }
      modalOpen={ modalOpen }
      handleModalOpen={ setModalOpen }
      validationCodeValue={ validationCodeValue }
      validationCodePending={ validationCodePending }
      validators={ validators }
      register={ register }
      rhfErrors={ rhfErrors }
      setValue={ setValue }
      control={ control }
      recipientEmail={ recipientEmailValue }
    />
  );
});

Addresses.displayName = 'Addresses';

export default Addresses;
